import { unzipSync, zipSync } from "fflate";
import type { BoardDef, MapDef, ModuleModel, SlotCategory } from "./buildfile";
import { boardKey } from "./buildfile";
import { buildPieceFromTypeState, encodeStateChain, splitChain, typeId } from "./piece-type";
import { SequenceDecoder, SequenceEncoder, TYPE_DELIM } from "./sequence";
import { decodeNotesCommand, emptyNotes, encodeNotesCommands, type NotesState } from "./notes";
import type { GamePiece } from "../model/piece";

export const COMMAND_SEPARATOR = "\x1b";

export interface MapImportState {
  boardImage: string | null;
  pieces: GamePiece[];
}

export interface VassalSaveState {
  mapStates: Map<string, MapImportState>;
  preservedCommands: string[];
  notes: NotesState;
  savedataXml: string;
  moduledataXml: string;
}

export interface VassalSaveBuildInput {
  model: ModuleModel;
  mapStates: Map<string, MapImportState>;
  preservedCommands: string[];
  notes: NotesState;
  /** Commands regenerated by the app each save (e.g. the TURN tracker state). */
  extraCommands?: string[];
}

export interface ParsedBoardPickerCommand {
  mapName: string;
  tiles: Array<{ boardName: string; x: number; y: number; reversed: boolean }>;
}

/** One board of an ASLBoardPicker `bd` command (grid cell, not pixels). */
export interface BoardPickerTile {
  /** ASLBoard crop bounds (x1,y1,x2,y2; -1 = unbounded), when present. */
  crop?: { x1: number; y1: number; x2: number; y2: number };
  col: number;
  row: number;
  boardName: string;
  reversed: boolean;
}

interface AddPieceCommand {
  id: string;
  type: string;
  state: string;
}

interface BasicPieceRuntimeState {
  mapId: string | null;
  x: number;
  y: number;
  gpid: string;
  persistent: Array<{ key: string; value: string }>;
}

const textDecoder = new TextDecoder("utf-8");
const asciiDecoder = new TextDecoder("ascii");
const textEncoder = new TextEncoder();

const REGENERATED_PREFIXES = [
  "begin_save",
  "end_save",
  "+/",
  "D/",
  "M/",
  "-/",
  "NOTES\t",
  "PNOTES\t",
  "PNOTE\t",
  "SNOTE\t",
];

export function decodeVassalSavedGame(bytes: Uint8Array): string {
  const header = asciiDecoder.decode(bytes.slice(0, 5));
  if (header !== "!VCSK") return textDecoder.decode(bytes);
  const key = Number.parseInt(asciiDecoder.decode(bytes.slice(5, 7)), 16);
  if (!Number.isFinite(key)) return "";
  const decoded = new Uint8Array(Math.floor((bytes.length - 7) / 2));
  for (let i = 7, j = 0; i + 1 < bytes.length; i += 2, j++) {
    decoded[j] = Number.parseInt(String.fromCharCode(bytes[i], bytes[i + 1]), 16) ^ key;
  }
  return textDecoder.decode(decoded);
}

export function encodeVassalSavedGame(text: string, key = 0x5a): Uint8Array {
  const bytes = textEncoder.encode(text);
  const body = Array.from(bytes, (byte) => (byte ^ key).toString(16).padStart(2, "0")).join("");
  return textEncoder.encode(`!VCSK${key.toString(16).padStart(2, "0")}${body}`);
}

export function readVassalSave(bytes: Uint8Array, model: ModuleModel): VassalSaveState {
  const zip = unzipSync(bytes);
  const savedGame = zip.savedGame ?? zip["savedGame"];
  if (!savedGame) throw new Error("Not a VASSAL save: missing savedGame entry.");

  return importVassalSaveText(decodeVassalSavedGame(savedGame), model, {
    savedataXml: xmlEntry(zip.savedata ?? zip["savedata"]),
    moduledataXml: xmlEntry(zip.moduledata ?? zip["moduledata"]),
  });
}

export function writeVassalSave(input: VassalSaveBuildInput): Uint8Array {
  const savedGame = buildVassalSaveText(input);
  return zipSync({
    savedGame: encodeVassalSavedGame(savedGame),
    savedata: textEncoder.encode(saveDataXml(input.model)),
    moduledata: textEncoder.encode(moduleDataXml(input.model)),
  });
}

export function importVassalSaveText(
  text: string,
  model: ModuleModel,
  opts: { savedataXml?: string; moduledataXml?: string } = {},
): VassalSaveState {
  const mapStates = new Map<string, MapImportState>();
  const preservedCommands: string[] = [];
  const notes = emptyNotes();
  const piecesById = new Map<string, GamePiece>();

  for (const command of splitCommands(text)) {
    if (!command || command === "\\" || command === "begin_save" || command === "end_save") continue;

    const boardPicker = parseBoardPickerCommand(command);
    if (boardPicker) {
      applyBoardPicker(mapStates, model.maps, boardPicker);
      continue;
    }

    const bdTiles = parseBdBoardCommand(command);
    if (bdTiles) {
      // Consumed only when a board applied; otherwise keep the original
      // command so a re-save doesn't lose the layout.
      if (!applyBdBoardCommand(mapStates, model.maps, bdTiles, command)) preservedCommands.push(command);
      continue;
    }

    if (command.startsWith("+/")) {
      const parsed = parseAddPieceCommand(command);
      if (!parsed) {
        preservedCommands.push(command);
        continue;
      }
      if (typeId(parsed.type) === "deck;") {
        preservedCommands.push(command);
        continue;
      }
      // Stacks (Java Stack.TYPE): state is `mapId;x;y;memberId;…`. Members
      // whose own state carries a map keep it and the stack is dropped (a
      // preserved stack would pin them to the stale stack point in the jar).
      // But modern saves park stacked pieces with NO map of their own — those
      // adopt the stack's map and position here, or they'd never appear.
      if (parsed.type === "stack") {
        const st = new SequenceDecoder(parsed.state, ";");
        const stackMap = normalizeMapName(st.nextTokenOr(""), model.maps);
        const sx = st.nextInt(0);
        const sy = st.nextInt(0);
        while (stackMap && st.hasMoreTokens()) {
          // The raw stack state ends with a bare backslash (state-chain
          // artifact), which would cling to the last member id.
          const member = piecesById.get(st.nextTokenOr("").replace(/\\+$/, ""));
          if (!member || member.mapId) continue;
          member.mapId = stackMap;
          member.x = sx;
          member.y = sy;
          member.trailStart = { x: sx, y: sy };
          stateFor(mapStates, stackMap).pieces.push(member);
          const idx = preservedCommands.indexOf(member.sourceDef);
          if (idx >= 0) preservedCommands.splice(idx, 1);
        }
        continue;
      }
      const piece = buildPieceFromTypeState(parsed.type, parsed.state);
      const basic = parseBasicPieceState(parsed.state);
      piece.sourceDef = command;
      piece.gpid = basic.gpid || parsed.id;
      piece.mapId = normalizeMapName(basic.mapId, model.maps) ?? "";
      piece.x = basic.x;
      piece.y = basic.y;
      piece.paletteCategory = classifyImportedPiece(piece);
      piece.trailStart = { x: piece.x, y: piece.y };
      piecesById.set(parsed.id, piece);
      if (piece.mapId) stateFor(mapStates, piece.mapId).pieces.push(piece);
      else preservedCommands.push(command);
      continue;
    }

    if (command.startsWith("D/")) {
      applyChangePiece(command, piecesById);
      continue;
    }

    if (command.startsWith("M/")) {
      applyMovePiece(command, piecesById, mapStates, model.maps);
      continue;
    }

    if (command.startsWith("-/")) {
      applyRemovePiece(command, piecesById, mapStates);
      continue;
    }

    if (decodeNotesCommand(command, notes)) continue;

    preservedCommands.push(command);
  }

  return {
    mapStates,
    preservedCommands,
    notes,
    savedataXml: opts.savedataXml ?? "",
    moduledataXml: opts.moduledataXml ?? "",
  };
}

export function buildVassalSaveText(input: VassalSaveBuildInput): string {
  const commands: string[] = ["begin_save"];
  for (const map of input.model.maps) {
    const state = input.mapStates.get(map.name);
    const boardCommand = state?.boardImage ? encodeBoardPicker(map, state.boardImage) : null;
    if (boardCommand) commands.push(boardCommand);
  }

  for (const map of input.model.maps) {
    const state = input.mapStates.get(map.name);
    if (!state) continue;
    for (const piece of state.pieces) {
      commands.push(encodeAddPieceForSave(piece, map.name));
    }
  }

  const preserved = input.preservedCommands.filter((command) => !isRegeneratedCommand(command));
  commands.push(...preserved);
  commands.push(...(input.extraCommands ?? []));
  commands.push(...encodeNotesCommands(input.notes));
  commands.push("end_save");
  return commands.join(COMMAND_SEPARATOR);
}

export function parseBoardPickerCommand(command: string): ParsedBoardPickerCommand | null {
  const fields = command.split("\t");
  const head = fields[0] ?? "";
  if (!head.endsWith("BoardPicker") || fields.length < 4) return null;
  const mapName = head.substring(0, head.length - "BoardPicker".length) || "Table";
  const tiles: ParsedBoardPickerCommand["tiles"] = [];
  for (let i = 1; i + 2 < fields.length; i += 3) {
    const nameAndReverse = new SequenceDecoder(fields[i], "/");
    const boardName = nameAndReverse.nextTokenOr("");
    const reversed = nameAndReverse.nextTokenOr("") === "rev";
    const x = Number.parseInt(fields[i + 1], 10);
    const y = Number.parseInt(fields[i + 2], 10);
    if (!boardName || !Number.isFinite(x) || !Number.isFinite(y)) break;
    tiles.push({ boardName, x, y, reversed });
  }
  return tiles.length ? { mapName, tiles } : null;
}

/**
 * ASLBoardPicker save command: `bd\t` + board descriptors joined by
 * `bd\t`. Each descriptor is `col \t row \t name` optionally followed by crop
 * bounds (`x \t y \t w \t h`), `VER \t version`, overlays and SSR tokens
 * (ASLBoardPicker.decode / ASLBoard.getState). A leading `r` on the name means
 * reversed — except the boards literally named `r` / `rdx`.
 */
export function parseBdBoardCommand(command: string): BoardPickerTile[] | null {
  if (!command.startsWith("bd\t")) return null;
  const tiles: BoardPickerTile[] = [];
  for (const desc of command.substring(3).split("bd\t")) {
    // Java uses StringTokenizer("\t\n"): empty tokens are skipped.
    const f = desc.split(/[\t\n]/).filter((s) => s !== "");
    if (f.length < 3) continue;
    const col = Number.parseInt(f[0], 10);
    const row = Number.parseInt(f[1], 10);
    if (!Number.isFinite(col) || !Number.isFinite(row)) continue;
    let boardName = f[2];
    let reversed = false;
    if (boardName.startsWith("r") && !/^(r|rdx)$/i.test(boardName)) {
      boardName = boardName.substring(1);
      reversed = true;
    }
    // Optional crop bounds follow the name: x1 y1 x2 y2 (-1 = unbounded).
    const nums = f.slice(3, 7).map((t) => Number.parseInt(t, 10));
    const crop = nums.length === 4 && nums.every((n) => Number.isFinite(n))
      ? { x1: nums[0], y1: nums[1], x2: nums[2], y2: nums[3] }
      : undefined;
    tiles.push({ col, row, boardName, reversed, ...(crop ? { crop } : {}) });
  }
  return tiles.length ? tiles : null;
}

export function parseAddPieceCommand(command: string): AddPieceCommand | null {
  if (!command.startsWith("+/")) return null;
  try {
    const dec = new SequenceDecoder(command.substring(2), "/");
    return {
      id: unwrapNull(dec.nextTokenOr("")),
      type: dec.nextTokenOr(""),
      state: dec.nextTokenOr(""),
    };
  } catch {
    return null;
  }
}

export function parseBasicPieceState(stateChain: string): BasicPieceRuntimeState {
  const basic = splitChain(stateChain).at(-1) ?? "";
  const dec = new SequenceDecoder(basic, ";");
  const mapId = unwrapNull(dec.nextTokenOr("null"));
  const x = dec.nextInt(0);
  const y = dec.nextInt(0);
  const gpid = dec.nextTokenOr("");
  const count = dec.nextInt(0);
  const persistent: BasicPieceRuntimeState["persistent"] = [];
  for (let i = 0; i < count && dec.hasMoreTokens(); i++) {
    const key = dec.nextTokenOr("");
    const value = dec.nextTokenOr("");
    persistent.push({ key, value });
  }
  return { mapId, x, y, gpid, persistent };
}

export function replaceBasicPieceState(stateChain: string, next: BasicPieceRuntimeState): string {
  const states = splitChain(stateChain);
  if (!states.length) return encodeBasicPieceState(next);
  states[states.length - 1] = encodeBasicPieceState(next);
  return joinChain(states);
}

/**
 * Apply a `bd` command. The command names no map — the ASLBoardPicker
 * belongs to the module's board-directory map. We render one
 * board at a time, so pick the first non-Blank board (`Blank0`/`Blank1` are
 * filler margins around the play area); the full multi-board layout is a known
 * simplification.
 */
function applyBdBoardCommand(
  mapStates: Map<string, MapImportState>,
  maps: MapDef[],
  tiles: BoardPickerTile[],
  command?: string,
): boolean {
  const map = maps.find((m) => m.boards.some((b) => b.archiveFile != null)) ?? maps[0];
  if (!map) return false;
  // Blank boards are pure layout fillers: they occupy grid cells (so other
  // tiles keep their positions) but draw nothing.
  const real = tiles
    .filter((t) => !/^blank/i.test(t.boardName))
    .map((t) => ({ t, src: map.boards.find((b) => b.name === t.boardName) }))
    .filter((r): r is { t: BoardPickerTile; src: BoardDef } => r.src != null);
  if (!real.length) return false;

  if (real.length === 1) {
    // Single real board: select it directly, with its crop applied.
    const { t, src } = real[0];
    const c = cropRect(t);
    if (c) src.crop = c;
    src.bdCommand = command;
    stateFor(mapStates, map.name).boardImage = boardKey(src);
    return true;
  }

  // Multi-board layout: a composed board of lazily-loaded tiles at their
  // (col,row) cells — same machinery as stock BoardPicker composed setups.
  const id = "bd:" + real.map(({ t }) => `${t.col},${t.row},${t.reversed ? "r" : ""}${t.boardName}`).join("+");
  let board = map.boards.find((b) => b.id === id);
  if (!board) {
    board = {
      id,
      name: real.map(({ t }) => (t.reversed ? "r" : "") + t.boardName).join("+"),
      image: "",
      reversible: false,
      tiles: real.map(({ t, src }) => ({
        boardName: t.boardName,
        image: src.image || "",
        x: t.col,
        y: t.row,
        ...(t.reversed ? { reversed: true } : {}),
        ...(cropRect(t) ? { crop: cropRect(t)! } : {}),
        ...(src.archiveFile ? { archiveFile: src.archiveFile } : {}),
      })),
    };
    map.boards.push(board);
  }
  board.bdCommand = command;
  stateFor(mapStates, map.name).boardImage = boardKey(board);
  return true;
}

/** Origin-anchored crop from a bd descriptor; moved-origin crops are ignored
 *  (they'd shift grid alignment). */
function cropRect(t: BoardPickerTile): { x: number; y: number; width: number; height: number } | null {
  const c = t.crop;
  if (!c || c.x1 !== 0 || c.y1 !== 0 || (c.x2 <= 0 && c.y2 <= 0)) return null;
  return { x: 0, y: 0, width: c.x2 > 0 ? c.x2 : 0, height: c.y2 > 0 ? c.y2 : 0 };
}

function applyBoardPicker(mapStates: Map<string, MapImportState>, maps: MapDef[], parsed: ParsedBoardPickerCommand): void {
  const map = maps.find((m) => m.name === parsed.mapName || m.buttonName === parsed.mapName);
  if (!map) return;
  const board = findOrCreateComposedBoard(map, parsed);
  stateFor(mapStates, map.name).boardImage = boardKey(board);
}

function applyChangePiece(command: string, piecesById: Map<string, GamePiece>): void {
  const dec = new SequenceDecoder(command.substring(2), "/");
  const id = dec.nextTokenOr("");
  const nextState = dec.nextTokenOr("");
  const piece = piecesById.get(id);
  if (!piece || !nextState) return;
  const currentBasic = parseBasicPieceState(encodeStateChain(piece));
  const nextBasic = parseBasicPieceState(nextState);
  for (const trait of piece.traits) trait.setState("");
  const rebuilt = buildPieceFromTypeState(commandType(piece), nextState);
  piece.x = nextBasic.x;
  piece.y = nextBasic.y;
  piece.gpid = nextBasic.gpid || currentBasic.gpid;
  for (let i = 0; i < piece.traits.length && i < rebuilt.traits.length; i++) {
    piece.traits[i].setState(rebuilt.traits[i].getState());
  }
}

function applyMovePiece(
  command: string,
  piecesById: Map<string, GamePiece>,
  mapStates: Map<string, MapImportState>,
  maps: MapDef[],
): void {
  const dec = new SequenceDecoder(command.substring(2), "/");
  const id = unwrapNull(dec.nextTokenOr(""));
  const newMap = normalizeMapName(unwrapNull(dec.nextTokenOr("null")), maps);
  const x = dec.nextInt(0);
  const y = dec.nextInt(0);
  const piece = piecesById.get(id);
  if (!piece || !newMap) return;
  removePieceFromStates(piece, mapStates);
  piece.mapId = newMap;
  piece.x = x;
  piece.y = y;
  stateFor(mapStates, newMap).pieces.push(piece);
}

function applyRemovePiece(command: string, piecesById: Map<string, GamePiece>, mapStates: Map<string, MapImportState>): void {
  const id = command.substring(2);
  const piece = piecesById.get(id);
  if (!piece) return;
  removePieceFromStates(piece, mapStates);
  piecesById.delete(id);
}

function encodeAddPieceForSave(piece: GamePiece, mapName: string): string {
  const parsed = parseAddPieceCommand(piece.sourceDef);
  const id = parsed?.id || piece.instanceId;
  const type = parsed?.type || commandType(piece);
  const state = replaceBasicPieceState(encodeStateChain(piece), {
    ...parseBasicPieceState(encodeStateChain(piece)),
    mapId: mapName,
    x: Math.round(piece.x),
    y: Math.round(piece.y),
    gpid: piece.gpid,
  });
  const enc = new SequenceEncoder("/");
  enc.append(id).append(type).append(state);
  return `+/${enc.getValue()}`;
}

function encodeBoardPicker(map: MapDef, boardImage: string): string | null {
  const board = map.boards.find((b) => boardKey(b) === boardImage || b.image === boardImage || b.name === boardImage);
  if (!board) return null;
  // Board-archive modules restore boards through ASLBoardPicker, which only decodes its own
  // `bd` format (ASLBoard.getState) — a stock BoardPicker command would be
  // ignored by the jar. Layouts that came from a save re-emit the original
  // command verbatim (crop/VER/overlay tokens survive); a manually picked
  // board emits the minimal form (Java defaults to uncropped).
  if (board.bdCommand) return board.bdCommand;
  if (board.archiveFile) return `bd\t0\t0\t${board.name}\t`;
  if (board.id?.startsWith("bd:") && board.tiles?.length) {
    return "bd\t" + board.tiles.map((t) => `${t.x}\t${t.y}\t${t.reversed ? "r" : ""}${t.boardName}\t`).join("bd\t");
  }
  const enc = new SequenceEncoder("\t", `${map.name}BoardPicker`);
  const tiles = board.tiles?.length ? board.tiles : [{ boardName: board.name, x: 0, y: 0 }];
  for (const tile of tiles) {
    enc.append(tile.boardName).append(tile.x).append(tile.y);
  }
  return enc.getValue();
}

function findOrCreateComposedBoard(map: MapDef, parsed: ParsedBoardPickerCommand): BoardDef {
  if (parsed.tiles.length === 1) {
    const single = map.boards.find((b) => b.name === parsed.tiles[0].boardName);
    if (single) return single;
  }

  const id = `save:${parsed.tiles.map((t) => `${t.boardName}@${t.x},${t.y}${t.reversed ? "r" : ""}`).join("|")}`;
  const existing = map.boards.find((b) => b.id === id);
  if (existing) return existing;

  const tiles = parsed.tiles
    .map((tile) => {
      const board = map.boards.find((b) => b.name === tile.boardName);
      if (!board?.image) return null;
      return { boardName: tile.boardName, image: board.image, x: tile.x, y: tile.y };
    })
    .filter((tile): tile is { boardName: string; image: string; x: number; y: number } => tile != null);

  const board: BoardDef = {
    id,
    name: parsed.tiles.map((t) => t.boardName).join(" + "),
    image: "",
    reversible: false,
    tiles,
  };
  map.boards.unshift(board);
  return board;
}

function stateFor(mapStates: Map<string, MapImportState>, mapName: string): MapImportState {
  let state = mapStates.get(mapName);
  if (!state) {
    state = { boardImage: null, pieces: [] };
    mapStates.set(mapName, state);
  }
  return state;
}

function removePieceFromStates(piece: GamePiece, states: Map<string, MapImportState>): void {
  for (const state of states.values()) {
    const index = state.pieces.indexOf(piece);
    if (index >= 0) state.pieces.splice(index, 1);
  }
}

function classifyImportedPiece(piece: GamePiece): SlotCategory {
  if (piece.hasHtmlLabel()) return "aids";
  const layer = piece.layerName();
  if (layer && /terrain|obstruction|building/i.test(layer)) return "terrain";
  if (/(deck|card|objective|victory|deployment)/i.test(piece.name())) return "aids";
  return "models";
}

function normalizeMapName(mapId: string | null, maps: MapDef[]): string | null {
  if (!mapId) return null;
  const map = maps.find((m) => m.name === mapId || m.buttonName === mapId);
  if (map) return map.name;
  // Java UniqueIdManager: a Map serializes as its name when it has one, else
  // as its id "Map<buildIndex>" — and lookups match name OR id. Old saves
  // (VASSAL 3.2 era) recorded the id even for named maps.
  const indexed = /^Map(\d+)$/.exec(mapId);
  if (indexed) return maps[Number(indexed[1])]?.name ?? mapId;
  return mapId;
}

function encodeBasicPieceState(state: BasicPieceRuntimeState): string {
  const enc = new SequenceEncoder(";");
  enc.append(state.mapId ?? "null").append(state.x).append(state.y).append(state.gpid).append(state.persistent.length);
  for (const prop of state.persistent) enc.append(prop.key).append(prop.value);
  return enc.getValue();
}

function joinChain(states: string[]): string {
  let acc: string | null = null;
  for (let i = states.length - 1; i >= 0; i--) {
    acc = acc == null ? states[i] : `${states[i]}${TYPE_DELIM}${escapeLevel(acc)}`;
  }
  return acc ?? "";
}

function commandType(piece: GamePiece): string {
  const parsed = parseAddPieceCommand(piece.sourceDef);
  return parsed?.type ?? "";
}

function isRegeneratedCommand(command: string): boolean {
  if (REGENERATED_PREFIXES.some((prefix) => command.startsWith(prefix))) return true;
  return parseBoardPickerCommand(command) != null;
}

function splitCommands(text: string): string[] {
  return text.split(COMMAND_SEPARATOR);
}

function unwrapNull(s: string): string {
  return s === "null" ? "" : s;
}

function escapeLevel(inner: string): string {
  let out = "";
  for (const ch of inner) {
    if (ch === TYPE_DELIM) out += "\\";
    out += ch;
  }
  return out;
}

function xmlEntry(bytes?: Uint8Array): string {
  return bytes ? textDecoder.decode(bytes) : "";
}

function saveDataXml(model: ModuleModel): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n<data version="1">\n  <version>${escapeXml(model.version)}</version>\n  <extra1/>\n  <extra2/>\n  <VassalVersion>3.7.24</VassalVersion>\n  <dateSaved>${Date.now()}</dateSaved>\n  <description/>\n</data>\n`;
}

function moduleDataXml(model: ModuleModel): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n<data version="1">\n  <version>${escapeXml(model.version)}</version>\n  <VassalVersion>3.7.24</VassalVersion>\n  <dateSaved>${Date.now()}</dateSaved>\n  <description/>\n  <name>${escapeXml(model.name)}</name>\n</data>\n`;
}

function escapeXml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
